import {HoistService, InitContext, LoadSpec, managed, PlainObject, TaskObserver, XH} from '@xh/hoist/core';
import {getDefaultOptions, parseDateTime} from '../../core/ServiceUtils';
import {Position} from '../data/DataTypes';
import {Cube} from '@xh/hoist/data';
import {allPositionFields} from '../cmp/PositionColumns';
import {bufferTime, Observable} from 'rxjs';
import {isEmpty} from 'lodash';
import {NotificationMessage} from '../data/MessageTypes';
import {wait} from '@xh/hoist/promise';

export class LivePositionsService extends HoistService {
    apiTask = TaskObserver.trackLast();
    @managed cube = this.createCube();

    override async initAsync(ctx: InitContext): Promise<void> {
        console.log('initializing LivePositionsService');
        new Observable(subscriber => {
            XH.messageHub.subscribe('portfolio', this.xhId, (message: NotificationMessage) => subscriber.next(message));
        })
            .pipe(bufferTime(100))
            .subscribe((messages: NotificationMessage[]) => {
                const updates = [];
                for (const message of messages) {
                    switch (message.envelope.eventType) {
                        case 'ADD':
                        case 'UPDATE':
                            updates.push(...message.payload.positions);
                            break;
                        case 'DELETE':
                        case 'RELOAD':
                            this.loadAsync().linkTo(this.apiTask);
                            return;
                    }
                }

                const positions = updates.map(p => this.processRawPosition(p));

                if (!isEmpty(updates)) {
                    this.cube.updateDataAsync(positions);
                    wait(900).then(() => {
                        const ids = positions.map(p => p.id),
                            current = ids.map(id => this.cube.store.getById(id));
                        current.forEach(rec => (rec.data.prevPriceTick = rec.data.price));
                        this.cube.updateDataAsync(current.map(rec => rec.data));
                    });
                }
            });

        await this.loadAsync();
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const positions = await this.fetchPortfolioPositionsAsync();
        this.cube.loadDataAsync(positions.map(p => this.processRawPosition(p)));
    }

    private createCube(): Cube {
        return new Cube({
            fields: allPositionFields
        });
    }

    private processRawPosition(position: Position): PlainObject {
        const portfolio = XH.portfolioService.portfoliosById.get(position.positionKey.portfolioId),
            dataObj = {
                ...position,
                id: `${position.portfolioId}-${position.securityId}`,
                portfolioName: portfolio?.portfolioName,
                portfolioManagementType: portfolio?.managementType,
                portfolioTaxType: portfolio?.taxableType,
                portfolioHoldingType: portfolio?.holdingsType,
                brokerage: portfolio?.brokerage,
                updatedOn: parseDateTime(position.updated),
                prevPriceTick: position.price
            },
            existing = this.cube.store.getById(dataObj.id);

        if (existing) {
            dataObj.prevPriceTick = existing.data.price;
        }

        return dataObj;
    }

    private async fetchPortfolioPositionsAsync(): Promise<Position[]> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/livepositions`,
            ...getDefaultOptions()
        });
    }
}
